import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
    createZustandIdbStateStorageWithTtl,
    EXPLORER_SESSION_TTL_MS,
} from './createZustandIdbStateStorageWithTtl';
import { addDaysYmd, chileanTodayYmd } from '../miContenido/metricsReportPeriod';
import type { LivestreamChannelCategory } from '../pages/Explorer/utils';
import {
    CONTENT_CATEGORY_ALL,
    type BrandSortColumn,
    type CanalesSortColumn,
    type LivestreamRankingMode,
    type LivestreamSortColumn,
    type ProgramSortColumn,
    type SocialBrandSortColumn,
    type SocialClipPlacementFilter,
    type SocialPlatformFilter,
    type SocialSortColumn,
    normalizeSocialClipPlacement,
    type SortDirection,
} from '../pages/Explorer/utils';
import type { TrendGranularity } from '../pages/Explorer/TrendChartCard';

// Tab indices: 0 Programas/YouTube, 2 Auspiciadores, 3 Livestreams, 4 Comparar
export type ExplorerTabIndex = 0 | 2 | 3 | 4;

export type ViewMode = 'programs' | 'canales';

/** Single channel-type value. `all` is every type. */
export type ProgramsChannelType = 'all' | 'digital' | 'tv' | 'noticias';

/** Channel types the Programas filter can check. An empty list is Todas. */
export type ProgramsChannelTypeOption = Exclude<ProgramsChannelType, 'all'>;

export const PROGRAMS_CHANNEL_TYPE_OPTIONS: readonly ProgramsChannelTypeOption[] = [
    'tv',
    'noticias',
    'digital',
];

/** Keep a stable subset. Checking every type collapses back to Todas. */
export function normalizeProgramsChannelTypes(value: unknown): ProgramsChannelTypeOption[] {
    const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
    const picked = new Set<ProgramsChannelTypeOption>();
    for (const item of raw) {
        if (typeof item !== 'string') continue;
        const trimmed = item.trim();
        if (trimmed === 'tv' || trimmed === 'noticias' || trimmed === 'digital') {
            picked.add(trimmed);
        }
    }
    if (picked.size === PROGRAMS_CHANNEL_TYPE_OPTIONS.length) return [];
    return PROGRAMS_CHANNEL_TYPE_OPTIONS.filter((option) => picked.has(option));
}

export function isOnlyProgramsChannelType(
    types: readonly ProgramsChannelTypeOption[],
    type: ProgramsChannelTypeOption,
): boolean {
    return types.length === 1 && types[0] === type;
}

/** Query value for a checkbox channel-type filter. Empty is Todas. */
export function programsChannelTypeParam(
    types: readonly ProgramsChannelTypeOption[],
): string | undefined {
    return types.length ? types.join(',') : undefined;
}

export const LIVESTREAM_CHANNEL_TYPE_OPTIONS: readonly LivestreamChannelCategory[] = [
    'tv',
    'noticias',
    'radio',
    'digital',
];

/** Checked livestream channel types. An empty list is Todas. */
export function normalizeLivestreamChannelTypes(value: unknown): LivestreamChannelCategory[] {
    const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
    const picked = new Set<LivestreamChannelCategory>();
    for (const item of raw) {
        if (typeof item !== 'string') continue;
        const trimmed = item.trim();
        if (
            trimmed === 'tv' ||
            trimmed === 'noticias' ||
            trimmed === 'radio' ||
            trimmed === 'digital'
        ) {
            picked.add(trimmed);
        }
    }
    if (picked.size === 0 || picked.size === LIVESTREAM_CHANNEL_TYPE_OPTIONS.length) return [];
    return LIVESTREAM_CHANNEL_TYPE_OPTIONS.filter((option) => picked.has(option));
}

/** Query value for the livestream checkbox filter. Empty is Todas. */
export function livestreamChannelTypeParam(
    types: readonly LivestreamChannelCategory[],
): string | undefined {
    return types.length ? types.join(',') : undefined;
}

export type SponsorViewMode = 'ranking' | 'compare';
export type SocialViewMode = 'ranking' | 'compare' | 'sponsors' | 'heatmap';
export type SponsorMediaFormat = 'ondemand' | 'clips';

export type LivestreamViewMode = 'ranking' | 'heatmap';

export type CompareMode = 'programas' | 'canales';

/** Slim program option for compare workspace persistence. */
export interface SlimProgramOption {
    _id: string;
    name: string;
    imageS3Key?: string;
    podcast: {
        _id: string;
        title: string;
        imageS3Key?: string;
        imageUrl?: string;
    };
}

/** Slim podcast/channel option for compare workspace persistence. */
export interface SlimPodcastOption {
    _id: string;
    title: string;
    imageS3Key?: string;
    imageUrl?: string;
}

/** Slim brand option for sponsors compare persistence. */
export interface SlimBrandOption {
    _id: string;
    name: string;
    category?: string;
    s3Key?: string;
    logoUrl?: string;
}

/** Slim social channel option for clips compare persistence. */
export interface SlimSocialChannelOption {
    _id: string;
    name: string;
    imageS3Key?: string;
}

export function slimProgramOption(p: unknown): SlimProgramOption | null {
    if (p == null || typeof p !== 'object') return null;
    const o = p as Record<string, unknown>;
    if (typeof o._id !== 'string' || typeof o.name !== 'string') return null;
    const podcast = o.podcast;
    if (podcast == null || typeof podcast !== 'object') return null;
    const pod = podcast as Record<string, unknown>;
    if (typeof pod._id !== 'string' || typeof pod.title !== 'string') return null;
    const out: SlimProgramOption = {
        _id: o._id,
        name: o.name,
        podcast: { _id: pod._id, title: pod.title },
    };
    if (typeof o.imageS3Key === 'string') out.imageS3Key = o.imageS3Key;
    if (typeof pod.imageS3Key === 'string') out.podcast.imageS3Key = pod.imageS3Key;
    if (typeof pod.imageUrl === 'string') out.podcast.imageUrl = pod.imageUrl;
    return out;
}

export function slimPodcastOption(p: unknown): SlimPodcastOption | null {
    if (p == null || typeof p !== 'object') return null;
    const o = p as Record<string, unknown>;
    if (typeof o._id !== 'string' || typeof o.title !== 'string') return null;
    const out: SlimPodcastOption = { _id: o._id, title: o.title };
    if (typeof o.imageS3Key === 'string') out.imageS3Key = o.imageS3Key;
    if (typeof o.imageUrl === 'string') out.imageUrl = o.imageUrl;
    return out;
}

export function slimBrandOption(b: unknown): SlimBrandOption | null {
    if (b == null || typeof b !== 'object') return null;
    const o = b as Record<string, unknown>;
    if (typeof o._id !== 'string' || typeof o.name !== 'string') return null;
    const out: SlimBrandOption = { _id: o._id, name: o.name };
    if (typeof o.category === 'string') out.category = o.category;
    if (typeof o.s3Key === 'string') out.s3Key = o.s3Key;
    if (typeof o.logoUrl === 'string') out.logoUrl = o.logoUrl;
    return out;
}

export function slimSocialChannelOption(c: unknown): SlimSocialChannelOption | null {
    if (c == null || typeof c !== 'object') return null;
    const o = c as Record<string, unknown>;
    if (typeof o._id !== 'string' || typeof o.name !== 'string') return null;
    const out: SlimSocialChannelOption = { _id: o._id, name: o.name };
    if (typeof o.imageS3Key === 'string') out.imageS3Key = o.imageS3Key;
    return out;
}

export function buildDefaultExplorerDates(
    minDate: string,
    maxDate: string,
): { startDate: string; endDate: string } {
    let endDate = chileanTodayYmd();
    if (endDate > maxDate) endDate = maxDate;
    if (endDate < minDate) endDate = minDate;
    let startDate = addDaysYmd(endDate, -28);
    if (startDate < minDate) startDate = minDate;
    if (startDate > endDate) {
        startDate = minDate;
        endDate = maxDate;
    }
    return { startDate, endDate };
}

const PLACEHOLDER_DATES = (): { startDate: string; endDate: string } => {
    // Placeholders until auth bounds are ready; initializeSessionDefaults replaces these.
    const today = chileanTodayYmd();
    return buildDefaultExplorerDates('2025-01-01', today);
};

export interface ProgramsRankingFilters {
    searchInput: string;
    selectedPrograms: SlimProgramOption[];
    contentCategoryFilter: string[];
    sortColumn: ProgramSortColumn;
    sortDirection: SortDirection;
    page: number;
    rowsPerPage: number;
    trendGranularity: TrendGranularity;
}

export interface CanalesRankingFilters {
    searchInput: string;
    selectedPodcasts: SlimPodcastOption[];
    sortColumn: CanalesSortColumn;
    sortDirection: SortDirection;
    page: number;
    rowsPerPage: number;
}

export interface SocialRankingFilters {
    searchInput: string;
    selectedChannels: SlimSocialChannelOption[];
    sortColumn: SocialSortColumn;
    sortDirection: SortDirection;
    page: number;
    rowsPerPage: number;
    platform: SocialPlatformFilter;
    channelType: ProgramsChannelTypeOption[];
}

export interface SocialSponsorsFilters {
    searchInput: string;
    selectedBrands: SlimBrandOption[];
    sortColumn: SocialBrandSortColumn;
    sortDirection: SortDirection;
    page: number;
    rowsPerPage: number;
    /** Clip implementation. `all` includes tagged, overlay, and placement. */
    placementType: SocialClipPlacementFilter;
}

export interface BrandsRankingFilters {
    searchInput: string;
    selectedBrands: SlimBrandOption[];
    sortColumn: BrandSortColumn;
    sortDirection: SortDirection;
    page: number;
    rowsPerPage: number;
    trendGranularity: TrendGranularity;
    /** Checked podcast channel types. Empty keeps every channel in the ranking. */
    channelType: ProgramsChannelTypeOption[];
}

export interface LivestreamsRankingFilters {
    searchInput: string;
    selectedChannels: SlimSocialChannelOption[];
    selectedPrograms: SlimSocialChannelOption[];
    sortColumn: LivestreamSortColumn;
    sortDirection: SortDirection;
    rankingMode: LivestreamRankingMode;
    page: number;
    rowsPerPage: number;
}

export interface ProgramsCompareFilters {
    mode: CompareMode;
    selectedPrograms: SlimProgramOption[];
    selectedPodcasts: SlimPodcastOption[];
    programSearchQ: string;
    podcastSearchQ: string;
    contentCategoryFilter: string[];
}

export interface SponsorsCompareFilters {
    selectedBrands: SlimBrandOption[];
    searchQ: string;
    categoryFilter: string;
}

export interface SocialCompareFilters {
    selectedChannels: SlimSocialChannelOption[];
}

const DEFAULT_PROGRAMS_RANKING: ProgramsRankingFilters = {
    searchInput: '',
    selectedPrograms: [],
    contentCategoryFilter: [CONTENT_CATEGORY_ALL],
    sortColumn: 'viewsPerEp',
    sortDirection: 'desc',
    page: 0,
    rowsPerPage: 20,
    trendGranularity: 'weekly',
};

const DEFAULT_CANALES_RANKING: CanalesRankingFilters = {
    searchInput: '',
    selectedPodcasts: [],
    sortColumn: 'totalViews',
    sortDirection: 'desc',
    page: 0,
    rowsPerPage: 20,
};

const DEFAULT_SOCIAL_RANKING: SocialRankingFilters = {
    searchInput: '',
    selectedChannels: [],
    sortColumn: 'views',
    sortDirection: 'desc',
    page: 0,
    rowsPerPage: 20,
    platform: 'all',
    channelType: [],
};

const DEFAULT_SOCIAL_SPONSORS: SocialSponsorsFilters = {
    searchInput: '',
    selectedBrands: [],
    sortColumn: 'views',
    sortDirection: 'desc',
    page: 0,
    rowsPerPage: 20,
    placementType: 'all',
};

function normalizeSocialSortColumn(value: unknown): SocialSortColumn {
    if (
        value === 'name' ||
        value === 'posts' ||
        value === 'views' ||
        value === 'viewsPerPost' ||
        value === 'interactions' ||
        value === 'engagement'
    ) {
        return value;
    }
    if (typeof value === 'string') {
        if (value.endsWith('.posts')) return 'posts';
        if (value.endsWith('.interactions')) return 'interactions';
        if (value.endsWith('.views')) return 'views';
    }
    return 'views';
}

function normalizeSocialBrandSortColumn(value: unknown): SocialBrandSortColumn {
    if (
        value === 'name' ||
        value === 'clips' ||
        value === 'channels' ||
        value === 'views' ||
        value === 'viewsPerClip' ||
        value === 'interactions'
    ) {
        return value;
    }
    return 'views';
}

function normalizeSocialPlatform(value: unknown): SocialPlatformFilter {
    if (
        value === 'facebook' ||
        value === 'tiktok' ||
        value === 'instagram' ||
        value === 'youtubeShorts'
    ) {
        return value;
    }
    return 'all';
}


const DEFAULT_BRANDS_RANKING: BrandsRankingFilters = {
    searchInput: '',
    selectedBrands: [],
    sortColumn: 'totalViews',
    sortDirection: 'desc',
    page: 0,
    rowsPerPage: 20,
    trendGranularity: 'weekly',
    channelType: [],
};

const DEFAULT_LIVESTREAMS_RANKING: LivestreamsRankingFilters = {
    searchInput: '',
    selectedChannels: [],
    selectedPrograms: [],
    sortColumn: 'totalViews',
    sortDirection: 'desc',
    rankingMode: 'channels',
    page: 0,
    rowsPerPage: 20,
};

const DEFAULT_PROGRAMS_COMPARE: ProgramsCompareFilters = {
    mode: 'programas',
    selectedPrograms: [],
    selectedPodcasts: [],
    programSearchQ: '',
    podcastSearchQ: '',
    contentCategoryFilter: [CONTENT_CATEGORY_ALL],
};

const DEFAULT_SPONSORS_COMPARE: SponsorsCompareFilters = {
    selectedBrands: [],
    searchQ: '',
    categoryFilter: '',
};

const DEFAULT_SOCIAL_COMPARE: SocialCompareFilters = {
    selectedChannels: [],
};

type ExplorerPersisted = {
    savedAt: number;
    viewMode: ViewMode;
    /** Checked types on Programas. Empty is Todas. */
    programsChannelType: ProgramsChannelTypeOption[];
    startDate: string;
    endDate: string;
    livestreamCategoryFilter: LivestreamChannelCategory[];
    sponsorViewMode: SponsorViewMode;
    programViewMode: SponsorViewMode;
    socialViewMode: SocialViewMode;
    sponsorMediaFormat: SponsorMediaFormat;
    livestreamViewMode: LivestreamViewMode;
    sponsorCategoryFilter: string;
    programsRanking: ProgramsRankingFilters;
    canalesRanking: CanalesRankingFilters;
    socialRanking: SocialRankingFilters;
    socialSponsors: SocialSponsorsFilters;
    brandsRanking: BrandsRankingFilters;
    livestreamsRanking: LivestreamsRankingFilters;
    programsCompare: ProgramsCompareFilters;
    sponsorsCompare: SponsorsCompareFilters;
    socialCompare: SocialCompareFilters;
};

interface ExplorerState extends ExplorerPersisted {
    /** Set to true when persist has rehydrated from storage; do not persist this. */
    _hasRehydrated: boolean;
    /** True after initializeSessionDefaults ran for a fresh session (savedAt === 0). */
    _sessionInitialized: boolean;
    /** Ranking total in the page title when Clips IA is off. Not persisted. */
    headerCount: number | null;
    setHeaderCount: (count: number | null) => void;
    setViewMode: (mode: ViewMode) => void;
    setProgramsChannelType: (channelType: ProgramsChannelTypeOption[]) => void;
    setStartDate: (date: string) => void;
    setEndDate: (date: string) => void;
    resetDates: (minDate: string, maxDate: string) => void;
    setLivestreamCategoryFilter: (c: readonly LivestreamChannelCategory[]) => void;
    setSponsorViewMode: (mode: SponsorViewMode) => void;
    setProgramViewMode: (mode: SponsorViewMode) => void;
    setSocialViewMode: (mode: SocialViewMode) => void;
    setSponsorMediaFormat: (format: SponsorMediaFormat) => void;
    setLivestreamViewMode: (mode: LivestreamViewMode) => void;
    setSponsorCategoryFilter: (category: string) => void;

    setProgramsRanking: (patch: Partial<ProgramsRankingFilters>) => void;
    setCanalesRanking: (patch: Partial<CanalesRankingFilters>) => void;
    setSocialRanking: (patch: Partial<SocialRankingFilters>) => void;
    setSocialSponsors: (patch: Partial<SocialSponsorsFilters>) => void;
    setBrandsRanking: (patch: Partial<BrandsRankingFilters>) => void;
    setLivestreamsRanking: (patch: Partial<LivestreamsRankingFilters>) => void;
    setProgramsCompare: (patch: Partial<ProgramsCompareFilters>) => void;
    setSponsorsCompare: (patch: Partial<SponsorsCompareFilters>) => void;
    setSocialCompare: (patch: Partial<SocialCompareFilters>) => void;

    /**
     * Fresh session (first visit or TTL expired): set today back 4 weeks
     * and reset table filters. Does not bump savedAt — clock starts on first user edit.
     */
    initializeSessionDefaults: (minDate: string, maxDate: string) => void;

    /**
     * Sliding idle clock: refresh savedAt only after the user has already edited
     * the session. Browsing a fresh default session does not start the TTL.
     */
    touchActivity: () => void;

    resetToDefaults: () => void;
}

function touch(
    set: (
        partial:
            | Partial<ExplorerState>
            | ((state: ExplorerState) => Partial<ExplorerState>),
    ) => void,
    patch: Partial<ExplorerPersisted> | ((state: ExplorerState) => Partial<ExplorerPersisted>),
) {
    set((state) => {
        const next = typeof patch === 'function' ? patch(state) : patch;
        return { ...next, savedAt: Date.now() };
    });
}

function filterDefaultsForReset(
    dates: { startDate: string; endDate: string },
): Partial<ExplorerPersisted> {
    return {
        savedAt: 0,
        viewMode: 'canales',
        programsChannelType: [],
        startDate: dates.startDate,
        endDate: dates.endDate,
        livestreamCategoryFilter: [],
        sponsorViewMode: 'ranking',
        programViewMode: 'ranking',
        socialViewMode: 'ranking',
        sponsorMediaFormat: 'ondemand',
        livestreamViewMode: 'ranking',
        sponsorCategoryFilter: '',
        programsRanking: { ...DEFAULT_PROGRAMS_RANKING },
        canalesRanking: { ...DEFAULT_CANALES_RANKING },
        socialRanking: { ...DEFAULT_SOCIAL_RANKING },
        socialSponsors: { ...DEFAULT_SOCIAL_SPONSORS },
        brandsRanking: { ...DEFAULT_BRANDS_RANKING },
        livestreamsRanking: { ...DEFAULT_LIVESTREAMS_RANKING },
        programsCompare: {
            ...DEFAULT_PROGRAMS_COMPARE,
            selectedPrograms: [],
            selectedPodcasts: [],
            contentCategoryFilter: [CONTENT_CATEGORY_ALL],
        },
        sponsorsCompare: {
            ...DEFAULT_SPONSORS_COMPARE,
            selectedBrands: [],
        },
        socialCompare: {
            ...DEFAULT_SOCIAL_COMPARE,
            selectedChannels: [],
        },
    };
}

// Used by onRehydrateStorage to call set when rehydration completes (set not in config scope)
let explorerSetState: ((partial: Partial<ExplorerState>) => void) | null = null;

export const useExplorerStore = create<ExplorerState>()(
    persist(
        (set) => {
            explorerSetState = set;
            const placeholders = PLACEHOLDER_DATES();
            return {
                _hasRehydrated: false,
                _sessionInitialized: false,
                headerCount: null,
                setHeaderCount: (count) => set({ headerCount: count }),
                savedAt: 0,
                viewMode: 'canales',
                programsChannelType: [] as ProgramsChannelTypeOption[],
                startDate: placeholders.startDate,
                endDate: placeholders.endDate,
                livestreamCategoryFilter: [] as LivestreamChannelCategory[],
                sponsorViewMode: 'ranking' as SponsorViewMode,
                programViewMode: 'ranking' as SponsorViewMode,
                socialViewMode: 'ranking' as SocialViewMode,
                sponsorMediaFormat: 'ondemand' as SponsorMediaFormat,
                livestreamViewMode: 'ranking' as LivestreamViewMode,
                sponsorCategoryFilter: '',
                programsRanking: { ...DEFAULT_PROGRAMS_RANKING },
                canalesRanking: { ...DEFAULT_CANALES_RANKING },
                socialRanking: { ...DEFAULT_SOCIAL_RANKING },
                socialSponsors: { ...DEFAULT_SOCIAL_SPONSORS },
                brandsRanking: { ...DEFAULT_BRANDS_RANKING },
                livestreamsRanking: { ...DEFAULT_LIVESTREAMS_RANKING },
                programsCompare: {
                    ...DEFAULT_PROGRAMS_COMPARE,
                    selectedPrograms: [],
                    selectedPodcasts: [],
                    contentCategoryFilter: [CONTENT_CATEGORY_ALL],
                },
                sponsorsCompare: {
                    ...DEFAULT_SPONSORS_COMPARE,
                    selectedBrands: [],
                },
                socialCompare: {
                    ...DEFAULT_SOCIAL_COMPARE,
                    selectedChannels: [],
                },

                setViewMode: (mode) => touch(set, { viewMode: mode }),
                setProgramsChannelType: (channelType) =>
                    touch(set, { programsChannelType: normalizeProgramsChannelTypes(channelType) }),
                setStartDate: (date) => touch(set, { startDate: date }),
                setEndDate: (date) => touch(set, { endDate: date }),
                resetDates: (minDate, maxDate) =>
                    touch(set, { startDate: minDate, endDate: maxDate }),
                setLivestreamCategoryFilter: (c) =>
                    touch(set, {
                        livestreamCategoryFilter: normalizeLivestreamChannelTypes(c),
                    }),
                setSponsorViewMode: (mode) => touch(set, { sponsorViewMode: mode }),
                setProgramViewMode: (mode) => touch(set, { programViewMode: mode }),
                setSocialViewMode: (mode) => touch(set, { socialViewMode: mode }),
                setSponsorMediaFormat: (format) => touch(set, { sponsorMediaFormat: format }),
                setLivestreamViewMode: (mode) => touch(set, { livestreamViewMode: mode }),
                setSponsorCategoryFilter: (category) =>
                    touch(set, { sponsorCategoryFilter: category }),

                setProgramsRanking: (patch) =>
                    touch(set, (s) => ({
                        programsRanking: {
                            ...s.programsRanking,
                            ...patch,
                            ...(patch.selectedPrograms
                                ? {
                                      selectedPrograms: patch.selectedPrograms
                                          .map(slimProgramOption)
                                          .filter((x): x is SlimProgramOption => x != null),
                                  }
                                : {}),
                        },
                    })),
                setCanalesRanking: (patch) =>
                    touch(set, (s) => ({
                        canalesRanking: {
                            ...s.canalesRanking,
                            ...patch,
                            ...(patch.selectedPodcasts
                                ? {
                                      selectedPodcasts: patch.selectedPodcasts
                                          .map(slimPodcastOption)
                                          .filter((x): x is SlimPodcastOption => x != null),
                                  }
                                : {}),
                        },
                    })),
                setSocialRanking: (patch) =>
                    touch(set, (s) => ({
                        socialRanking: {
                            ...s.socialRanking,
                            ...patch,
                            ...(patch.selectedChannels
                                ? {
                                      selectedChannels: patch.selectedChannels
                                          .map(slimSocialChannelOption)
                                          .filter((x): x is SlimSocialChannelOption => x != null),
                                  }
                                : {}),
                            ...(patch.channelType
                                ? { channelType: normalizeProgramsChannelTypes(patch.channelType) }
                                : {}),
                        },
                    })),
                setSocialSponsors: (patch) =>
                    touch(set, (s) => ({
                        socialSponsors: {
                            ...s.socialSponsors,
                            ...patch,
                            ...(patch.selectedBrands
                                ? {
                                      selectedBrands: patch.selectedBrands
                                          .map(slimBrandOption)
                                          .filter((x): x is SlimBrandOption => x != null),
                                  }
                                : {}),
                        },
                    })),
                setBrandsRanking: (patch) =>
                    touch(set, (s) => ({
                        brandsRanking: {
                            ...s.brandsRanking,
                            ...patch,
                            ...(patch.selectedBrands
                                ? {
                                      selectedBrands: patch.selectedBrands
                                          .map(slimBrandOption)
                                          .filter((x): x is SlimBrandOption => x != null),
                                  }
                                : {}),
                            ...(patch.channelType
                                ? { channelType: normalizeProgramsChannelTypes(patch.channelType) }
                                : {}),
                        },
                    })),
                setLivestreamsRanking: (patch) =>
                    touch(set, (s) => ({
                        livestreamsRanking: {
                            ...s.livestreamsRanking,
                            ...patch,
                            ...(patch.selectedChannels
                                ? {
                                      selectedChannels: patch.selectedChannels
                                          .map(slimSocialChannelOption)
                                          .filter((x): x is SlimSocialChannelOption => x != null),
                                  }
                                : {}),
                            ...(patch.selectedPrograms
                                ? {
                                      selectedPrograms: patch.selectedPrograms
                                          .map(slimSocialChannelOption)
                                          .filter((x): x is SlimSocialChannelOption => x != null),
                                  }
                                : {}),
                        },
                    })),
                setProgramsCompare: (patch) =>
                    touch(set, (s) => {
                        const next: Partial<ProgramsCompareFilters> = { ...patch };
                        if (patch.selectedPrograms) {
                            next.selectedPrograms = patch.selectedPrograms
                                .map(slimProgramOption)
                                .filter((x): x is SlimProgramOption => x != null);
                        }
                        if (patch.selectedPodcasts) {
                            next.selectedPodcasts = patch.selectedPodcasts
                                .map(slimPodcastOption)
                                .filter((x): x is SlimPodcastOption => x != null);
                        }
                        return {
                            programsCompare: { ...s.programsCompare, ...next },
                        };
                    }),
                setSponsorsCompare: (patch) =>
                    touch(set, (s) => {
                        const next: Partial<SponsorsCompareFilters> = { ...patch };
                        if (patch.selectedBrands) {
                            next.selectedBrands = patch.selectedBrands
                                .map(slimBrandOption)
                                .filter((x): x is SlimBrandOption => x != null);
                        }
                        return {
                            sponsorsCompare: { ...s.sponsorsCompare, ...next },
                        };
                    }),
                setSocialCompare: (patch) =>
                    touch(set, (s) => {
                        const next: Partial<SocialCompareFilters> = { ...patch };
                        if (patch.selectedChannels) {
                            next.selectedChannels = patch.selectedChannels
                                .map(slimSocialChannelOption)
                                .filter((x): x is SlimSocialChannelOption => x != null);
                        }
                        return {
                            socialCompare: { ...s.socialCompare, ...next },
                        };
                    }),

                initializeSessionDefaults: (minDate, maxDate) => {
                    const dates = buildDefaultExplorerDates(minDate, maxDate);
                    set({
                        ...filterDefaultsForReset(dates),
                        _sessionInitialized: true,
                    });
                },

                touchActivity: () => {
                    set((state) => {
                        if (!state.savedAt) return state;
                        return { savedAt: Date.now() };
                    });
                },

                resetToDefaults: () => {
                    const dates = PLACEHOLDER_DATES();
                    set({
                        ...filterDefaultsForReset(dates),
                        _sessionInitialized: false,
                    });
                },
            };
        },
        {
            name: 'explorer-store',
            version: 17,
            storage: createJSONStorage(() =>
                createZustandIdbStateStorageWithTtl(EXPLORER_SESSION_TTL_MS),
            ),
            partialize: (state): ExplorerPersisted => ({
                savedAt: state.savedAt,
                viewMode: state.viewMode,
                programsChannelType: state.programsChannelType,
                startDate: state.startDate,
                endDate: state.endDate,
                livestreamCategoryFilter: state.livestreamCategoryFilter,
                sponsorViewMode: state.sponsorViewMode,
                programViewMode: state.programViewMode,
                socialViewMode: state.socialViewMode,
                sponsorMediaFormat: state.sponsorMediaFormat,
                livestreamViewMode: state.livestreamViewMode,
                sponsorCategoryFilter: state.sponsorCategoryFilter,
                programsRanking: state.programsRanking,
                canalesRanking: state.canalesRanking,
                socialRanking: state.socialRanking,
                socialSponsors: state.socialSponsors,
                brandsRanking: state.brandsRanking,
                livestreamsRanking: state.livestreamsRanking,
                programsCompare: {
                    ...state.programsCompare,
                    selectedPrograms: state.programsCompare.selectedPrograms
                        .map(slimProgramOption)
                        .filter((x): x is SlimProgramOption => x != null),
                    selectedPodcasts: state.programsCompare.selectedPodcasts
                        .map(slimPodcastOption)
                        .filter((x): x is SlimPodcastOption => x != null),
                },
                sponsorsCompare: {
                    ...state.sponsorsCompare,
                    selectedBrands: state.sponsorsCompare.selectedBrands
                        .map(slimBrandOption)
                        .filter((x): x is SlimBrandOption => x != null),
                },
                socialCompare: {
                    ...state.socialCompare,
                    selectedChannels: state.socialCompare.selectedChannels
                        .map(slimSocialChannelOption)
                        .filter((x): x is SlimSocialChannelOption => x != null),
                },
            }),
            migrate: (persisted, fromVersion): ExplorerPersisted => {
                const p = (persisted ?? {}) as Partial<ExplorerPersisted> & {
                    rankingMode?: LivestreamRankingMode;
                };
                const placeholders = PLACEHOLDER_DATES();
                // v1 → v2: old indefinite prefs get a fresh savedAt so they aren't lost immediately
                const savedAt =
                    fromVersion < 2
                        ? Date.now()
                        : typeof p.savedAt === 'number'
                          ? p.savedAt
                          : 0;

                let rankingMode: LivestreamRankingMode =
                    p.livestreamsRanking?.rankingMode ?? 'channels';
                // One-time merge from legacy explorer-livestream-ui key
                if (typeof localStorage !== 'undefined') {
                    try {
                        const legacy = localStorage.getItem('explorer-livestream-ui');
                        if (legacy) {
                            const parsed = JSON.parse(legacy) as {
                                state?: { rankingMode?: LivestreamRankingMode };
                            };
                            if (
                                parsed?.state?.rankingMode === 'channels' ||
                                parsed?.state?.rankingMode === 'programs'
                            ) {
                                rankingMode = parsed.state.rankingMode;
                            }
                        }
                    } catch {
                        /* ignore */
                    }
                }

                return {
                    savedAt,
                    viewMode: p.viewMode === 'programs' ? 'programs' : 'canales',
                    programsChannelType:
                        fromVersion < 5
                            ? []
                            : normalizeProgramsChannelTypes(p.programsChannelType),
                    startDate:
                        fromVersion < 9
                            ? placeholders.startDate
                            : typeof p.startDate === 'string'
                              ? p.startDate
                              : placeholders.startDate,
                    endDate:
                        fromVersion < 9
                            ? placeholders.endDate
                            : typeof p.endDate === 'string'
                              ? p.endDate
                              : placeholders.endDate,
                    livestreamCategoryFilter:
                        fromVersion < 5
                            ? []
                            : normalizeLivestreamChannelTypes(p.livestreamCategoryFilter),
                    sponsorViewMode:
                        p.sponsorViewMode === 'compare' ? 'compare' : 'ranking',
                    programViewMode:
                        p.programViewMode === 'compare' ? 'compare' : 'ranking',
                    socialViewMode:
                        p.socialViewMode === 'compare'
                            ? 'compare'
                            : p.socialViewMode === 'sponsors'
                              ? 'sponsors'
                              : p.socialViewMode === 'heatmap'
                                ? 'heatmap'
                                : 'ranking',
                    sponsorMediaFormat: p.sponsorMediaFormat === 'clips' ? 'clips' : 'ondemand',
                    livestreamViewMode:
                        p.livestreamViewMode === 'heatmap' ? 'heatmap' : 'ranking',
                    sponsorCategoryFilter:
                        typeof p.sponsorCategoryFilter === 'string'
                            ? p.sponsorCategoryFilter
                            : '',
                    programsRanking: {
                        ...DEFAULT_PROGRAMS_RANKING,
                        ...(p.programsRanking ?? {}),
                        selectedPrograms: (p.programsRanking?.selectedPrograms ?? [])
                            .map(slimProgramOption)
                            .filter((x): x is SlimProgramOption => x != null),
                    },
                    canalesRanking: {
                        ...DEFAULT_CANALES_RANKING,
                        ...(p.canalesRanking ?? {}),
                        selectedPodcasts: (p.canalesRanking?.selectedPodcasts ?? [])
                            .map(slimPodcastOption)
                            .filter((x): x is SlimPodcastOption => x != null),
                    },
                    socialRanking: {
                        ...DEFAULT_SOCIAL_RANKING,
                        ...(p.socialRanking ?? {}),
                        sortColumn: normalizeSocialSortColumn(p.socialRanking?.sortColumn),
                        platform: normalizeSocialPlatform(p.socialRanking?.platform),
                        channelType: normalizeProgramsChannelTypes(p.socialRanking?.channelType),
                        selectedChannels: (p.socialRanking?.selectedChannels ?? [])
                            .map(slimSocialChannelOption)
                            .filter((x): x is SlimSocialChannelOption => x != null),
                    },
                    socialSponsors: {
                        ...DEFAULT_SOCIAL_SPONSORS,
                        ...(p.socialSponsors ?? {}),
                        sortColumn: normalizeSocialBrandSortColumn(p.socialSponsors?.sortColumn),
                        placementType: normalizeSocialClipPlacement(p.socialSponsors?.placementType),
                        selectedBrands: (p.socialSponsors?.selectedBrands ?? [])
                            .map(slimBrandOption)
                            .filter((x): x is SlimBrandOption => x != null),
                    },
                    brandsRanking: {
                        ...DEFAULT_BRANDS_RANKING,
                        ...(p.brandsRanking ?? {}),
                        channelType: normalizeProgramsChannelTypes(p.brandsRanking?.channelType),
                        selectedBrands: (p.brandsRanking?.selectedBrands ?? [])
                            .map(slimBrandOption)
                            .filter((x): x is SlimBrandOption => x != null),
                    },
                    livestreamsRanking: {
                        ...DEFAULT_LIVESTREAMS_RANKING,
                        ...(p.livestreamsRanking ?? {}),
                        selectedChannels: (p.livestreamsRanking?.selectedChannels ?? [])
                            .map(slimSocialChannelOption)
                            .filter((x): x is SlimSocialChannelOption => x != null),
                        selectedPrograms: (p.livestreamsRanking?.selectedPrograms ?? [])
                            .map(slimSocialChannelOption)
                            .filter((x): x is SlimSocialChannelOption => x != null),
                        rankingMode,
                        ...(fromVersion < 6
                            ? { sortColumn: 'totalViews' as const, sortDirection: 'desc' as const }
                            : {}),
                        ...(fromVersion < 14 && rankingMode === 'programs'
                            ? { sortColumn: 'avgViewers' as const, sortDirection: 'desc' as const }
                            : {}),
                    },
                    programsCompare: {
                        ...DEFAULT_PROGRAMS_COMPARE,
                        ...(p.programsCompare ?? {}),
                        selectedPrograms: (p.programsCompare?.selectedPrograms ?? [])
                            .map(slimProgramOption)
                            .filter((x): x is SlimProgramOption => x != null),
                        selectedPodcasts: (p.programsCompare?.selectedPodcasts ?? [])
                            .map(slimPodcastOption)
                            .filter((x): x is SlimPodcastOption => x != null),
                        contentCategoryFilter:
                            p.programsCompare?.contentCategoryFilter ?? [CONTENT_CATEGORY_ALL],
                    },
                    sponsorsCompare: {
                        ...DEFAULT_SPONSORS_COMPARE,
                        ...(p.sponsorsCompare ?? {}),
                        selectedBrands: (p.sponsorsCompare?.selectedBrands ?? [])
                            .map(slimBrandOption)
                            .filter((x): x is SlimBrandOption => x != null),
                    },
                    socialCompare: {
                        ...DEFAULT_SOCIAL_COMPARE,
                        ...(p.socialCompare ?? {}),
                        selectedChannels: (p.socialCompare?.selectedChannels ?? [])
                            .map(slimSocialChannelOption)
                            .filter((x): x is SlimSocialChannelOption => x != null),
                    },
                };
            },
            onRehydrateStorage: () => (state) => {
                if (explorerSetState) {
                    const patch: Partial<ExplorerState> = { _hasRehydrated: true };
                    // Fresh session (TTL expired / no storage): mark for initializeSessionDefaults
                    if (!state?.savedAt) {
                        patch.savedAt = 0;
                        patch._sessionInitialized = false;
                    } else {
                        patch._sessionInitialized = true;
                    }
                    explorerSetState(patch);
                }
            },
        },
    ),
);

export { EXPLORER_SESSION_TTL_MS };
